import React, { useMemo } from 'react';
import { Users, Circle, User, Crown, Settings, ArrowLeft } from 'lucide-react';
import { ProfileAvatar, UserProfilePopover } from '../ui';
import { useChatStore } from '../../store/chatStore';
import { useAuthStore } from '../../store/authStore';
import type { GroupMember } from '../../types';

interface MembersPanelProps {
  groupId: string;
  members: GroupMember[];
  currentGroup: any;
  onGroupSettingsClick?: () => void;
  onClose?: () => void;
  className?: string;
}

const MembersPanel: React.FC<MembersPanelProps> = ({
  groupId,
  members,
  currentGroup,
  onGroupSettingsClick,
  onClose,
  className = ''
}) => {
  const { getGroupOnlineUsers } = useChatStore();
  const { user } = useAuthStore();
  const onlineUsers = getGroupOnlineUsers(groupId);

  // Separate members into online and offline
  const { onlineMembers, offlineMembers } = useMemo(() => {
    const acceptedMembers = members.filter(member => member.status === 'ACCEPTED');
    const onlineUserIds = new Set(onlineUsers.map(u => u.id));

    const online = acceptedMembers.filter(member =>
      onlineUserIds.has(member.userId.toString())
    );
    const offline = acceptedMembers.filter(member =>
      !onlineUserIds.has(member.userId.toString())
    );

    return { onlineMembers: online, offlineMembers: offline };
  }, [members, onlineUsers]);

  const isOwner = user?.id === currentGroup?.ownerId;

  const renderMember = (member: GroupMember, isOnline: boolean) => (
    <UserProfilePopover
      key={member.userId}
      userId={member.userId}
      placement="left"
      trigger={
        <div className="flex items-center gap-3 hover:bg-white/[.05] rounded-item p-2 cursor-pointer transition-colors">
          {/* Avatar with online indicator */}
          <div className="relative">
            <ProfileAvatar
              user={member.user}
              size="sm"
            />

            {/* Online status indicator */}
            {isOnline && (
              <div className="absolute -bottom-0.5 -right-0.5">
                <Circle className="h-3 w-3 text-neon-lime fill-current" />
              </div>
            )}
          </div>

          {/* User info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1">
              <p className="text-sm font-medium text-fg-soft truncate">
                {member.user.username}
                {member.userId === user?.id && (
                  <span className="text-fg-muted"> (Jij)</span>
                )}
              </p>
              {member.userId === currentGroup?.ownerId && (
                <Crown className="h-3 w-3 text-neon-lime" />
              )}
            </div>
            <p className="text-xs text-fg-muted">
              {member.userId === currentGroup?.ownerId ? 'Eigenaar' : 'Lid'}
              {member.user.age && ` • ${member.user.age} jaar oud`}
            </p>
          </div>
        </div>
      }
    />
  );

  return (
    <div className={`bg-transparent ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[.06]">
        <div className="flex items-center gap-2">
          {/* Back to chat: the panel covers the chat on mobile */}
          {onClose && (
            <button
              onClick={onClose}
              className="sm:hidden p-2 -ml-2 rounded-lg text-fg-muted hover:text-fg-bright hover:bg-white/[.08] transition-colors"
              title="Terug naar chat"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
          )}
          <Users className="h-5 w-5 text-fg-faint" />
          <h3 className="text-sm font-semibold text-fg-bright">
            Leden ({members.filter(m => m.status === 'ACCEPTED').length})
          </h3>
        </div>

        {/* Group settings button for owners */}
        {isOwner && (
          <button
            onClick={onGroupSettingsClick}
            className="p-1.5 rounded-lg text-fg-faint hover:text-fg-bright hover:bg-white/[.08] transition-colors"
            title="Groepsinstellingen"
          >
            <Settings className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="p-4 space-y-6">
        {/* Online Members */}
        {onlineMembers.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="sf-dot-online" />
              <h4 className="font-mono text-[11px] font-medium uppercase tracking-[.16em] text-neon-lime">
                Online ({onlineMembers.length})
              </h4>
            </div>
            <div className="space-y-1">
              {onlineMembers.map(member => renderMember(member, true))}
            </div>
          </div>
        )}

        {/* Offline Members */}
        {offlineMembers.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <User className="h-4 w-4 text-fg-faint" />
              <h4 className="sf-label-mono">
                Offline ({offlineMembers.length})
              </h4>
            </div>
            <div className="space-y-1">
              {offlineMembers.map(member => renderMember(member, false))}
            </div>
          </div>
        )}

        {/* No members message */}
        {members.filter(m => m.status === 'ACCEPTED').length === 0 && (
          <div className="text-center py-6">
            <Users className="mx-auto h-8 w-8 text-fg-faint mb-2" />
            <p className="text-sm text-fg-muted italic">Nog geen leden</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MembersPanel;